import { useMemo, type ReactNode } from "react";

import { OpenInventoryDiscrepancySkuBadge } from "@/components/manufacturing/OpenInventoryDiscrepancySkuBadge";
import {
  SearchableSelect,
  type SearchableSelectOption,
  type SearchableSelectProps,
} from "@/components/ui/SearchableSelect";
import { useOpenInventoryDiscrepancySkus } from "@/lib/useOpenInventoryDiscrepancySkus";

type SkuSearchableSelectProps = Omit<SearchableSelectProps, "optionTrailing" | "selectedValueTrailing">;

/**
 * SearchableSelect for catalog SKUs with open inventory discrepancy badges on options and the selected value.
 */
export function SkuSearchableSelect(props: SkuSearchableSelectProps): JSX.Element {
  const { value, options } = props;

  const skusForLookup = useMemo(() => {
    const list = options.map((option) => option.value);
    if (value.trim() !== "") {
      list.push(value);
    }
    return list;
  }, [options, value]);

  const { hasOpenDiscrepancy } = useOpenInventoryDiscrepancySkus(skusForLookup);

  const renderBadge = (sku: string): ReactNode =>
    hasOpenDiscrepancy(sku) ? <OpenInventoryDiscrepancySkuBadge sku={sku} /> : null;

  return (
    <SearchableSelect
      {...props}
      optionTrailing={(option: SearchableSelectOption) => renderBadge(option.value)}
      selectedValueTrailing={value.trim() !== "" ? () => renderBadge(value) : undefined}
    />
  );
}
